/**
 * 発注ワークフロー API レスポンス → UI 型変換 Mapper
 *
 * API レスポンスの ISO 文字列 Date を `Date` オブジェクトに変換するのみ。
 * 派生フラグ(isOverdue / isReversible / isCancellable)はサーバー側で生成済の
 * 暫定値を素通しする(設計書 04-api-design §2.6 D-B)。
 *
 * 判定ロジックをクライアント側で重複させないため、ビジネス判定は一切行わない。
 */

import { DateFactory } from '@/shared/utils/dateFactory';
import type {
  PurchaseOrderViewModel,
  PurchaseOrderListResult,
  PurchaseOrderPagination,
  ConfirmArrivalResult,
  ReverseArrivalResult,
  PurchaseOrderInventoryTransactionResult,
  PurchaseOrderSiteInventorySummary,
  PurchaseOrderStatusValue,
  PurchaseOrderCancelReasonCode,
} from '../types/purchaseOrder';

// ========== API レスポンス生型 ==========

/**
 * API から返却される発注 ViewModel 生表現(ISO 文字列 Date を含む)
 */
export interface ApiPurchaseOrderViewModel {
  readonly id: number;
  readonly siteId: number;
  readonly siteName: string;
  readonly materialTypeId: number;
  readonly materialTypeCode: string;
  readonly materialTypeName: string;
  readonly orderedQuantity: number;
  readonly expectedArrivalDate: string;
  readonly orderNote: string | null;
  readonly status: PurchaseOrderStatusValue;
  readonly orderedByUserId: string;
  readonly orderedByUserName: string;
  readonly orderedAt: string;
  readonly arrivedByUserId: string | null;
  readonly arrivedByUserName: string | null;
  readonly arrivedAt: string | null;
  readonly arrivalNote: string | null;
  readonly inventoryTransactionId: number | null;
  readonly lastArrivedByUserId: string | null;
  readonly lastArrivedByUserName: string | null;
  readonly lastArrivedAt: string | null;
  readonly reversedByUserId: string | null;
  readonly reversedByUserName: string | null;
  readonly reversedAt: string | null;
  readonly reversalReason: string | null;
  readonly reversalCount: number;
  readonly cancelledByUserId: string | null;
  readonly cancelledByUserName: string | null;
  readonly cancelledAt: string | null;
  readonly cancelReasonCode: PurchaseOrderCancelReasonCode | null;
  readonly cancelReasonDetail: string | null;
  readonly isOverdue: boolean;
  readonly isReversible: boolean;
  readonly isCancellable: boolean;
}

export interface ApiPurchaseOrderListResult {
  readonly items: readonly ApiPurchaseOrderViewModel[];
  readonly pagination: PurchaseOrderPagination;
}

interface ApiInventoryTransactionResult {
  readonly transactionId: number;
  readonly quantityBefore: number;
  readonly quantityAfter: number;
}

interface ApiSiteInventorySummary {
  readonly currentQuantity: number;
  readonly pendingOrderedQuantity: number;
  readonly effectiveQuantity: number;
  readonly isLowStock: boolean;
  readonly isReorderRequired: boolean;
}

export interface ApiConfirmArrivalResult {
  readonly purchaseOrder: ApiPurchaseOrderViewModel;
  readonly inventoryTransaction: ApiInventoryTransactionResult;
  readonly currentInventory: ApiSiteInventorySummary;
}

export interface ApiReverseArrivalResult {
  readonly purchaseOrder: ApiPurchaseOrderViewModel;
  readonly inventoryTransaction: ApiInventoryTransactionResult;
  readonly currentInventory: ApiSiteInventorySummary;
}

// ========== 変換関数(API → UI 型) ==========

/**
 * ISO 日付文字列を `Date` に変換、null は null を返す
 *
 * faro-frontend は UI 層での `new Date()` 直接生成を禁じているため、
 * すべて `DateFactory.fromISOString` 経由で生成する(ドメイン層完結原則)。
 */
function parseNullableDate(params: { readonly raw: string | null }): Date | null {
  if (params.raw === null) {
    return null;
  }
  return DateFactory.fromISOString(params.raw);
}

/**
 * API ViewModel → UI ViewModel への変換
 */
export function mapApiPurchaseOrderToViewModel(params: {
  readonly api: ApiPurchaseOrderViewModel;
}): PurchaseOrderViewModel {
  const { api } = params;
  return {
    id: api.id,
    siteId: api.siteId,
    siteName: api.siteName,
    materialTypeId: api.materialTypeId,
    materialTypeCode: api.materialTypeCode,
    materialTypeName: api.materialTypeName,
    orderedQuantity: api.orderedQuantity,
    expectedArrivalDate: DateFactory.fromISOString(api.expectedArrivalDate),
    orderNote: api.orderNote,
    status: api.status,
    orderedByUserId: api.orderedByUserId,
    orderedByUserName: api.orderedByUserName,
    orderedAt: DateFactory.fromISOString(api.orderedAt),
    arrivedByUserId: api.arrivedByUserId,
    arrivedByUserName: api.arrivedByUserName,
    arrivedAt: parseNullableDate({ raw: api.arrivedAt }),
    arrivalNote: api.arrivalNote,
    inventoryTransactionId: api.inventoryTransactionId,
    lastArrivedByUserId: api.lastArrivedByUserId,
    lastArrivedByUserName: api.lastArrivedByUserName,
    lastArrivedAt: parseNullableDate({ raw: api.lastArrivedAt }),
    reversedByUserId: api.reversedByUserId,
    reversedByUserName: api.reversedByUserName,
    reversedAt: parseNullableDate({ raw: api.reversedAt }),
    reversalReason: api.reversalReason,
    reversalCount: api.reversalCount,
    cancelledByUserId: api.cancelledByUserId,
    cancelledByUserName: api.cancelledByUserName,
    cancelledAt: parseNullableDate({ raw: api.cancelledAt }),
    cancelReasonCode: api.cancelReasonCode,
    cancelReasonDetail: api.cancelReasonDetail,
    isOverdue: api.isOverdue,
    isReversible: api.isReversible,
    isCancellable: api.isCancellable,
  };
}

/**
 * API 一覧結果 → UI 一覧結果への変換
 */
export function mapApiListResultToViewModel(params: {
  readonly api: ApiPurchaseOrderListResult;
}): PurchaseOrderListResult {
  return {
    items: params.api.items.map((item) =>
      mapApiPurchaseOrderToViewModel({ api: item })
    ),
    pagination: params.api.pagination,
  };
}

/**
 * API InventoryTransactionResult → UI 型(変換不要、参照のみ)
 *
 * モジュール private(同ファイル内 mapApiConfirmArrivalResult / mapApiReverseArrivalResult からのみ呼出)。
 */
function mapApiInventoryTransactionResult(params: {
  readonly api: ApiInventoryTransactionResult;
}): PurchaseOrderInventoryTransactionResult {
  return {
    transactionId: params.api.transactionId,
    quantityBefore: params.api.quantityBefore,
    quantityAfter: params.api.quantityAfter,
  };
}

/**
 * API SiteInventorySummary → UI 型(変換不要、参照のみ)
 *
 * モジュール private(同ファイル内 mapApiConfirmArrivalResult / mapApiReverseArrivalResult からのみ呼出)。
 */
function mapApiSiteInventorySummary(params: {
  readonly api: ApiSiteInventorySummary;
}): PurchaseOrderSiteInventorySummary {
  return {
    currentQuantity: params.api.currentQuantity,
    pendingOrderedQuantity: params.api.pendingOrderedQuantity,
    effectiveQuantity: params.api.effectiveQuantity,
    isLowStock: params.api.isLowStock,
    isReorderRequired: params.api.isReorderRequired,
  };
}

/**
 * API 着荷確認結果 → UI 型
 */
export function mapApiConfirmArrivalResult(params: {
  readonly api: ApiConfirmArrivalResult;
}): ConfirmArrivalResult {
  return {
    purchaseOrder: mapApiPurchaseOrderToViewModel({ api: params.api.purchaseOrder }),
    inventoryTransaction: mapApiInventoryTransactionResult({
      api: params.api.inventoryTransaction,
    }),
    currentInventory: mapApiSiteInventorySummary({ api: params.api.currentInventory }),
  };
}

/**
 * API 着荷取消結果 → UI 型
 */
export function mapApiReverseArrivalResult(params: {
  readonly api: ApiReverseArrivalResult;
}): ReverseArrivalResult {
  return {
    purchaseOrder: mapApiPurchaseOrderToViewModel({ api: params.api.purchaseOrder }),
    inventoryTransaction: mapApiInventoryTransactionResult({
      api: params.api.inventoryTransaction,
    }),
    currentInventory: mapApiSiteInventorySummary({ api: params.api.currentInventory }),
  };
}
